import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PackageCheck, PlusCircle, RefreshCw, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import StatusBadge from "@/components/app/StatusBadge";

const REASONS = [
  { code: "dead_on_arrival", label: "Dead on arrival" },
  { code: "hardware_failure", label: "Hardware failure" },
  { code: "swap", label: "Warranty swap" },
  { code: "other", label: "Other" },
];

export default function RMA() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [receiveFor, setReceiveFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/rma", { params })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [statusFilter]);
  useEffect(() => { load(); }, [load, statusFilter]);

  const doClose = async (id) => {
    try { await api.post(`/rma/${id}/close`); toast.success("RMA closed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Lifecycle · Advanced</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">RMA · Return Merchandise Authorization</h1>
          <p className="text-sm text-slate-500 mt-0.5">Track replacement devices with full history preserved on the original asset.</p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-44" data-testid="rma-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="replacement_received">Replacement received</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900" data-testid="rma-new-btn"><PlusCircle className="h-4 w-4 mr-1.5" /> New RMA</Button>
            </DialogTrigger>
            <NewRMADialog onDone={() => { setShowCreate(false); load(); }} />
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5">Code</th>
              <th className="text-left px-4 py-2.5">Original device</th>
              <th className="text-left px-4 py-2.5">Replaced by</th>
              <th className="text-left px-4 py-2.5">Reason / Vendor</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No RMA cases</td></tr>
            ) : items.map((r) => (
              <tr key={r.id} data-testid={`rma-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{r.rma_code}</td>
                <td className="px-4 py-2.5">
                  {r.original_asset ? (<><div className="font-medium text-slate-800">{r.original_asset.brand} {r.original_asset.model}</div><div className="text-xs text-slate-500 font-mono">{r.original_asset.serial}</div></>) : "—"}
                </td>
                <td className="px-4 py-2.5">
                  {r.replacement_asset ? (
                    <div className="flex items-center gap-1.5">
                      <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                      <div>
                        <div className="font-medium text-slate-800">{r.replacement_asset.brand} {r.replacement_asset.model}</div>
                        <div className="text-xs text-slate-500 font-mono">{r.replacement_asset.serial}</div>
                      </div>
                    </div>
                  ) : <span className="text-slate-400 text-xs">Pending</span>}
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-700">
                  <div className="capitalize">{r.reason?.replace(/_/g, " ")}</div>
                  <div className="text-slate-400">{r.vendor || "—"}</div>
                </td>
                <td className="px-4 py-2.5"><StatusBadge value={r.status} /></td>
                <td className="px-4 py-2.5 text-right space-x-1.5">
                  {r.status === "open" && (
                    <Button size="sm" variant="outline" onClick={() => setReceiveFor(r)} data-testid={`rma-receive-${r.id}`}>
                      <PackageCheck className="h-3.5 w-3.5 mr-1" /> Receive replacement
                    </Button>
                  )}
                  {(r.status === "replacement_received") && (
                    <Button size="sm" className="bg-slate-900" onClick={() => doClose(r.id)} data-testid={`rma-close-${r.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Close
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {receiveFor && <ReceiveDialog rma={receiveFor} onClose={() => setReceiveFor(null)} onDone={() => { setReceiveFor(null); load(); }} />}
    </div>
  );
}

function NewRMADialog({ onDone }) {
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ original_asset_id: "", reason: "hardware_failure", vendor: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/assets/", { params: { q: q || undefined, limit: 20 } })
      .then(({ data }) => setAssets(data.items || []));
  }, [q]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/rma", form);
      toast.success("RMA opened");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>Open new RMA</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="text-xs">Original asset</Label>
          <Input placeholder="Search serial or tag…" value={q} onChange={(e) => setQ(e.target.value)} className="mt-1 h-9 mb-1.5" />
          <Select value={form.original_asset_id} onValueChange={(v) => setForm({ ...form, original_asset_id: v })}>
            <SelectTrigger className="h-10" data-testid="rma-original-asset-select"><SelectValue placeholder="Select asset…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.brand} {a.model} · {a.serial}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Reason</Label>
            <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
              <SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Vendor</Label>
            <Input className="mt-1 h-10" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Apple, Dell, HP…" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Notes</Label>
          <Textarea className="mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
          The original asset&apos;s status is left as-is — you may retire, dispose or reclaim it after the RMA is complete. Full history is preserved on the original via <span className="font-mono">replaced_by_asset_id</span>.
        </div>
      </div>
      <DialogFooter>
        <Button className="bg-slate-900" disabled={!form.original_asset_id || submitting} onClick={submit} data-testid="rma-submit-btn">
          {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
          Open RMA
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ReceiveDialog({ rma, onClose, onDone }) {
  const [mode, setMode] = useState("new"); // new | existing
  const [existing, setExisting] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({
    replacement_asset_id: "",
    replacement_serial: "",
    replacement_model: "",
    replacement_asset_tag: "",
    inherit_employee: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mode !== "existing") return;
    api.get("/assets/", { params: { q: q || undefined, limit: 20, available_only: true } })
      .then(({ data }) => setExisting(data.items || []));
  }, [q, mode]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { inherit_employee: form.inherit_employee };
      if (mode === "existing") payload.replacement_asset_id = form.replacement_asset_id;
      else {
        payload.replacement_serial = form.replacement_serial;
        payload.replacement_model = form.replacement_model || undefined;
        payload.replacement_asset_tag = form.replacement_asset_tag || undefined;
      }
      await api.post(`/rma/${rma.id}/receive`, payload);
      toast.success("Replacement recorded");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };
  const valid = mode === "existing" ? !!form.replacement_asset_id : !!form.replacement_serial;

  return (
    <Dialog open={!!rma} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Receive replacement for {rma.rma_code}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button variant={mode === "new" ? "default" : "outline"} className={mode === "new" ? "bg-slate-900" : ""} onClick={() => setMode("new")} data-testid="rma-receive-mode-new">New serial from vendor</Button>
            <Button variant={mode === "existing" ? "default" : "outline"} className={mode === "existing" ? "bg-slate-900" : ""} onClick={() => setMode("existing")} data-testid="rma-receive-mode-existing">Use existing asset</Button>
          </div>
          {mode === "new" ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Replacement serial</Label>
                <Input className="mt-1 h-10 font-mono" value={form.replacement_serial} onChange={(e) => setForm({ ...form, replacement_serial: e.target.value })} data-testid="rma-serial-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Model (optional)</Label>
                  <Input className="mt-1 h-10" value={form.replacement_model} onChange={(e) => setForm({ ...form, replacement_model: e.target.value })} placeholder={rma.original_asset?.model} />
                </div>
                <div>
                  <Label className="text-xs">Asset tag (optional)</Label>
                  <Input className="mt-1 h-10" value={form.replacement_asset_tag} onChange={(e) => setForm({ ...form, replacement_asset_tag: e.target.value })} />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Input placeholder="Search available asset…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" />
              <Select value={form.replacement_asset_id} onValueChange={(v) => setForm({ ...form, replacement_asset_id: v })}>
                <SelectTrigger className="h-10" data-testid="rma-existing-select"><SelectValue placeholder="Select replacement asset…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {existing.map((a) => <SelectItem key={a.id} value={a.id}>{a.brand} {a.model} · {a.serial}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.inherit_employee} onChange={(e) => setForm({ ...form, inherit_employee: e.target.checked })} />
            Automatically assign to the original device&apos;s employee
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-slate-900" disabled={!valid || submitting} onClick={submit} data-testid="rma-receive-submit">
            {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PackageCheck className="h-4 w-4 mr-1.5" />}
            Record replacement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
